<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Auction Settings</h3>
            </div>
        </div>

        <div class="px-4 py-5 sm:p-6">

            <Fieldset>
                <AccountsSelectCreate
                    label="Select a Seller (Optional)"
                    :auctioneer_id="asset_data.auctioneer_id"
                    :enable_create="true"
                    :account_type="'sellers'"
                    :existing_account_id="asset_data?.seller_account_id"
                    v-on:account_selected="account => sellerSelected(account)"
                />
            </Fieldset>

            <Fieldset>
                <AssetAssignSaleSectionSaleCodeDuplicate
                    v-if="asset_data"
                    :asset="asset_data"
                    v-on:sale_section_code_updated="saleSectionCodeUpdated"
                />
            </Fieldset>

            <Fieldset>
                <FormField namespace="asset" name="master_opening_bid" :field="form.asset.fields.master_opening_bid" class="mb-4"/>
                <FormField namespace="asset" name="bid_interval" :field="form.asset.fields.bid_interval" class="mb-4"/>
                <FormField namespace="asset" name="master_guide_price_min" :field="form.asset.fields.master_guide_price_min" class="mb-4"/>
                <FormField namespace="asset" name="master_guide_price_max" :field="form.asset.fields.master_guide_price_max" class="mb-4"/>
                <FormField namespace="asset" name="public_display_guide_price" :field="form.asset.fields.public_display_guide_price" />
            </Fieldset>

            <Fieldset heading="Lot Number">

                <div v-if="form.lot.fields.lot_number.value" class="flex justify-between items-center">
                    <h3 class="font-medium text-md text-gray-700">{{ `Assign Lot No: ${form.lot.fields.lot_number.value}` }}</h3>
                    <Button width="w-auto" size="sm" color="blue-outline" :onClick="() => resetLotnoState()">Clear</Button>
                </div>

                <div v-if="!form.lot.fields.lot_number.value" class="mb-8">
                    <form action="#" @submit.prevent="checkLotNumberAvailability">
                        <div class="flex items-stretch">
                            <FormField :field="form.lot_number.fields.requested_lotno" namespace="lot_number" :name="'requested_lotno'" class="w-full" />
                            <Button type="submit" width="w-full" color="blue-100" class="ml-2" :disabled="form.lot_number.loading">
                                <span>{{ form.lot_number.loading ? 'Checking availability' : 'Check availability' }}</span>
                                <LoadingIcon v-if="form.lot_number.loading" class="ml-2 mr-3 h-5 w-5 animate-reverse-spin" />
                            </Button>
                        </div>
                    </form>
                    <Message :active="state.search_message_heading" :heading="state.search_message_heading" :body="state.search_message_body" :type="state.search_message_success ? 'success' : 'error'" class="mt-2" />
                </div>

                <div v-if="!form.lot.fields.lot_number.value && state.suggested_lotnos">
                    <h3 class="block text-sm font-medium text-gray-700 mb-2">Suggested Lot Numbers</h3>
                    <Button v-for="lotno in state.suggested_lotnos" :onClick="() => setLotNumber(lotno)" :key="lotno" width="w-auto" size="sm" class="mr-2 mb-2">{{ lotno }}</Button>
                </div>

            </Fieldset>

        </div>

        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => submitStep()" :disabled="!next_button_active">Save and Finish</Button>
        </div>

    </div>
</template>

<script>
import { useAssetStore } from '@sto/asset.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'

import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import {isValid, getPayload, getUserOrganisationType, penceToPounds, poundsToPence} from '@lib/helpers'
import { useRoute } from 'vue-router'
import config from '@/config'

import LoadingIcon from '@ico/LoadingIcon.vue'
import AssetAssignSaleSectionSaleCodeDuplicate from "@par/asset/AssetAssignSaleSectionSaleCodeDuplicate.vue"
import InformationCircleIcon from "@ico/InformationCircleIcon.vue";
import AccountsSelectCreate from "@par/account/AccountsSelectCreate.vue";
import Fieldset from "@glo/Fieldset.vue";
import * as SaleService from "@ser/sale/index.js";
import FormField from "@glo/FormField.vue";
import Button from "@glo/Button.vue";

export default {
    components: {
        Button,
        FormField,
        Fieldset,
        AccountsSelectCreate,
        LoadingIcon,
        AssetAssignSaleSectionSaleCodeDuplicate,
        InformationCircleIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ASSET_STORE = useAssetStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const asset_id = computed(() => {
            return ASSET_STORE?.asset?.id || null
        })

        const asset_data = computed(() => {
            return ASSET_STORE?.asset || null
        })

        const next_button_active = computed(() => {
            return true
        })

        const state = reactive({
            sale_code_update_loading: false,
            search_message: null,
            search_message_success: false,
            search_message_heading: null,
            search_message_body: null,
            lotno_search_timeout: null,
            suggested_lotnos: []
        })

        const form = reactive({
            loading: false,
            // the sale code assignnment form
            sale_settings: {
                fields: {
                    sale_code_id: {
                        active: true,
                        label: 'Sale code',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    sale_section_id: {
                        active: true,
                        label: 'Sale code',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            },
            asset: {
                fields: {
                    master_opening_bid: {
                        active: true,
                        value: 0,
                        label: 'Opening bid',
                        type: 'number',
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    bid_interval: {
                        active: true,
                        value: 0,
                        label: 'Bid interval',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [
                            { type: 'min', value: 1 }
                        ],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    master_guide_price_min: {
                        active: true,
                        value: 0,
                        label: 'NET Guide Price Minimum',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    master_guide_price_max: {
                        active: true,
                        value: 0,
                        label: 'NET Guide Price Maximum',
                        type: 'number',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    public_display_guide_price: {
                        active: true,
                        label: 'Display guide price to public',
                        type: 'toggle',
                        value: true,
                        stored_value: true,
                        required: false,
                        pristine: true,
                        valid: true,
                        hide_hint: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    seller_id: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        validation: [],
                        valid: true
                    },
                    seller_account_id: {
                        active: true,
                        type: 'hidden',
                        value: null,
                        validation: [],
                        valid: true
                    }
                }
            },
            lot: {
                fields: {
                    lot_number: {
                        active: true,
                        label: 'Lot number',
                        type: 'hidden',
                        value: null,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    auctioneer_id: {
                        active: true,
                        label: '',
                        type: 'hidden',
                        value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            lot_number: {
                loading: false,
                fields: {
                    requested_lotno: {
                        active: true,
                        label: null,
                        type: 'number',
                        placeholder: 'Lot number',
                        value: '',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    }
                }
            }
        })

        const setSearchMessage = (success, heading, body) => {
            state.search_message_success = success || false
            state.search_message_heading = heading || null
            state.search_message_body = body || null
        }

        const saleSectionCodeUpdated = data => {
            form.sale_settings.fields.sale_code_id.value = data?.sale_code_id || null
            form.sale_settings.fields.sale_section_id.value = data?.sale_section_id || null
        }

        const saveAsset = async payload => {
            return ASSET_STORE.update(asset_data.value?.id, payload)
        }

        const saveLotNumber = async payload => {
            return ASSET_STORE.assignLotNumber(asset_data.value?.id, payload)
        }

        const saveSaleSectionCode = async payload => {
            return ASSET_STORE.assignSaleCode(asset_data.value?.auctioneer_id, asset_data.value?.id, payload)
        }

        const setAssetData = asset => {
            if (!asset) return

            if (asset.sale_code_id) {
                setAssetDataField(form.sale_settings.fields.sale_code_id, asset.sale_code_id)
            }

            if (asset.sale_code_id) {
                setAssetDataField(form.sale_settings.fields.sale_section_id, asset.sale_section_id)
            }

            if (asset.master_opening_bid) {
                setAssetDataField(form.asset.fields.master_opening_bid, penceToPounds(asset.master_opening_bid))
            }

            if (asset.bid_interval) {
                setAssetDataField(form.asset.fields.bid_interval, penceToPounds(asset.bid_interval))
            }

            if (asset.master_guide_price_min) {
                setAssetDataField(form.asset.fields.master_guide_price_min, penceToPounds(asset.master_guide_price_min))
            }

            if (asset.master_guide_price_max) {
                setAssetDataField(form.asset.fields.master_guide_price_max, penceToPounds(asset.master_guide_price_max))
            }

            if (asset.public_display_guide_price) {
                setAssetDataField(form.asset.fields.public_display_guide_price, asset.public_display_guide_price)
            }

            if (asset.seller_id) {
                setAssetDataField(form.asset.fields.seller_id, asset.seller_id)
            }

            if (asset.seller_account_id) {
                setAssetDataField(form.asset.fields.seller_account_id, asset.seller_account_id)
            }

            if (asset.lot_number) {
                setAssetDataField(form.lot.fields.lot_number, asset.lot_number.number)
            }

            if (asset.auctioneer_id) {
                setAssetDataField(form.lot.fields.auctioneer_id, asset.auctioneer_id)
            }
        }

        const setAssetDataField = (field, value) => {
            field.value = value
            field.stored_value = value
            field.pristine = false
            field.valid = true
        }

        const submitStep = async () => {

            if (form.loading) return

            // make sure everything is valid
            if (!isValid(form.sale_settings.fields) || !isValid(form.asset.fields) || !isValid(form.lot.fields)) return

            try {

                form.loading = true

                let promises = []

                if (form.sale_settings.fields.sale_section_id.value && form.sale_settings.fields.sale_code_id.value) {
                    const sale_payload = getPayload(form.sale_settings.fields)
                    promises.push(saveSaleSectionCode(sale_payload))
                }

                const asset_payload = getPayload(form.asset.fields)
                promises.push(saveAsset(asset_payload))

                if (!form.lot.fields.auctioneer_id.value) form.lot.fields.auctioneer_id.value = vehicle_asset.value?.auctioneer_id
                if (form.lot.fields.lot_number.value && form.lot.fields.auctioneer_id.value) {
                    const lot_payload = getPayload(form.lot.fields)
                    promises.push(saveLotNumber(lot_payload))
                }

                await Promise.all(promises)

            } catch (error) {
                console.error('save error', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem saving some of your details')

            } finally {
                await ASSET_STORE.showAndSetStore(asset_id.value)
                form.loading = false
                ASSET_STORE.setCreateModalState(true)
            }
        }

        const getSuggestedLotNos = async () => {

            const params = []
            params.push({key: 'auctioneer_id', value: asset_data.value?.auctioneer_id})
            const available = await SaleService.lotNumberAvailable(params)

            state.suggested_lotnos = available.suggested_lot_numbers
        }

        const sellerSelected = account => {
            form.asset.fields.seller_id.value = account?.seller?.id || null
            form.asset.fields.seller_account_id.value = account?.id || null
        }

        const checkLotNumberAvailability = async () => {

            if (form.lot_number.loading || !form.lot_number.fields.requested_lotno.value) return

            try {
                form.lot.fields.lot_number.value = null
                setSearchMessage(false, null, null)

                form.lot_number.loading = true

                let params = []
                params.push({key: 'auctioneer_id', value: asset_data.value?.auctioneer_id })
                if (form.lot_number.fields.requested_lotno.value) params.push({key: 'lot_number', value: form.lot_number.fields.requested_lotno.value})
                const available = await SaleService.lotNumberAvailable(params)

                state.suggested_lotnos = Array.isArray(available?.suggested_lot_numbers) ? available?.suggested_lot_numbers : state.suggested_lotnos

                if (available.is_available === false) {
                    setSearchMessage(false, 'Not available', `Lot number ${available.requested_lot_number} in use`)
                    form.lot_number.loading = false
                    return
                } else {
                    setSearchMessage(true, 'Available', `Lot number ${available.requested_lot_number} is available`)
                    form.lot_number.loading = false
                }

                if (!available.requested_lot_number) {
                    setSearchMessage(false, 'Not available', `Looks like something went wrong!`)
                    form.lot_number.loading = false
                    return
                }

                setLotNumber(available.requested_lot_number)

            } catch (error) {

                console.error('SaleService.lotNumberAvailable error', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem checking availability on that lont number')

            } finally {
                form.lot_number.loading = false
            }
        }

        const setLotNumber = lot_number => {
            form.lot.fields.lot_number.value = lot_number
        }

        const resetLotnoState = () => {
            form.lot.fields.lot_number.value = null
            state.lotno_search_timeout = null
            form.lot_number.fields.requested_lotno.value = null
            state.search_message_body = null
            state.search_message_success = false
            state.search_message_heading = null
        }

        watch(
            () => asset_data.value,
            (new_value, old_value) => {
                setAssetData(asset_data.value)
            }
        )

        watch(
            () => form.lot_number.fields.requested_lotno.value,
            () => {
                if (form.lot.fields.lot_number.value !== null) return
                if (state.lotno_search_timeout) clearTimeout(state.lotno_search_timeout)
                state.lotno_search_timeout = setTimeout(() => {
                    checkLotNumberAvailability()
                }, 1000)
            }
        )

        onMounted(() => {
            getSuggestedLotNos()
        })

        return {
            ASSET_STORE,
            asset_data,
            state,
            form,
            next_button_active,
            submitStep,
            saleSectionCodeUpdated,
            sellerSelected,
            checkLotNumberAvailability,
            setLotNumber,
            resetLotnoState
        }
    }
}
</script>

